import React, { useState, useEffect } from 'react';
import { sendTrackingInfo, getAvailableCreditsCount } from '@panel/commons';
import { hasCreditsTransition } from '@panel/commons/helpers';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Drawer from '@panel/commons/lib/Drawer';
import { userInfoSelectors } from '@panel/core/src/state/ducks/user';
import { formatAmount } from '@panel/commons/helpers/formatAmount';
import ProductsButton from '../../commons/ProductsButton';
import ProductsTable from '../../commons/ProductsTable';
import { creditsSelectors } from '../../../../ducks/credits';
import { PostingsSelectors } from '../../../../ducks/posting';
import { FilterSelectors, FilterOperations } from '../../../../ducks/filter';
import { TotalCount, ActionsRow, FilterButton, ContentLayout, NoCredits, AlertContainer } from './styles';
import Alert from '../../Alert/Alert';
import LockedSoftAlertMessage from '../../Alert/LockedSoftAlertMessage';
import { useDuplicateAlert } from '../../Alert/useDuplicateAlert';
import { useLockedSoftAlert } from '../../Alert/useLockedSoftAlert';
import { useLockedManualAlert } from '../../Alert/useLockedManualAlert';
import Filtro from '../../Filtro/Filtro';
import filterIcon from '../../../../assets/icons/filter-icon.svg';
import Sort from './Sort';
import useUrlSearchParams from '../../hooks/useUrlSearchParams';
import BannerI24Full from '../../PostingsListDesktop/BannerI24Full';

const siteId = process.env.REACT_APP_BUILD_SITE;
const isBrasil = siteId.includes('BR');

function PostingsHeader({ postingsCount = 0, hasFilters, isVAPublisher, publisherTypeId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [drawerVisibility, setDrawerVisibility] = useState(false);
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [displayNearEndAlert, setDisplayNearEndAlert] = useState(true);
  const [showBannerUnifiedPanel, setShowBannerUnifiedPanel] = useState(false);

  const { duplicatedCount, nearEndPostingCount } = useSelector(PostingsSelectors.getPostingsState);
  const { showDuplicateAlert, closeDuplicateAlert } = useDuplicateAlert(duplicatedCount);
  const { lockedSoft, lockedManual } = useSelector(userInfoSelectors.getuserInfoState);
  const { showLockedSoftAlert, closeLockedSoftAlert } = useLockedSoftAlert(lockedSoft);
  const { showLockedManualAlert } = useLockedManualAlert(lockedManual);
  const { cleanPartialUrlFilters } = useUrlSearchParams();
  const { appliedFilters } = useSelector(FilterSelectors.getFiltersState);
  const filtersData = useSelector(FilterSelectors.getFiltersState);
  const filtersCriteria = filtersData.searchCriteria;
  const appliedFiltersCount = Object.keys(appliedFilters).filter(
    filter => appliedFilters[filter].id !== '' && filter !== 'sort' && filter !== 'onlineFirst'
  ).length;

  const { credits, ...creditsState } = useSelector(creditsSelectors.getCreditsState);
  const { branchCredits, globalCredits } = credits;

  const isParticular = publisherTypeId === '1';

  const handleProducts = () => {
    setDrawerVisibility(!drawerVisibility);
    sendTrackingInfo('Panel', 'Mis avisos', 'Productos disponibles-Desplegar');
  };
  const onClose = () => setDrawerVisibility(false);

  const hasCredits = branchCredits && !!branchCredits[0].creditItems;
  const hasCreditFromUnifiedPanel = hasCreditsTransition(branchCredits, globalCredits);

  const handleNearEndCta = () => {
    dispatch(FilterOperations.partialResetFilters('status', { id: 'NEAR_END', displayName: 'NEAR_END' }));
    cleanPartialUrlFilters('NEAR_END', 'status');
    setDisplayNearEndAlert(false);
  };

  const handleFilterDuplicates = () => {
    dispatch(FilterOperations.partialResetFilters('hasDuplicates', { id: 'true', displayName: 'duplicated' }));
    cleanPartialUrlFilters('true', 'hasDuplicates');
  };

  const handleClickBannerVA = () => {
    dispatch(FilterOperations.partialResetFilters('publicationPlan', { id: 'FREE', displayName: 'FREE' }));
    cleanPartialUrlFilters('FREE', 'publicationPlan');
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Se valida que tenga el filtro publicationPlan aplicado:
    const publicationPlanFilterApplied =
      params &&
      params
        .toString()
        .split('&')
        .filter(item => item.split('=')[0] === 'publicationPlan')[0];

    const hasPlanFreeFilterApplied =
      publicationPlanFilterApplied && publicationPlanFilterApplied.split('=')[1] === 'FREE';

    // Se valida que el filtro publicationPlan aplicado no sea 'FREE' y que tenga créditos de transición para mostrar el banner con el CTA para aplicar el filtro de 'FREE':
    const isEnadledToShowBannerUnifiedPanels =
      !isVAPublisher && hasCreditFromUnifiedPanel && publicationPlanFilterApplied && !hasPlanFreeFilterApplied;

    setShowBannerUnifiedPanel(isEnadledToShowBannerUnifiedPanels);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersCriteria, hasCreditFromUnifiedPanel]);

  return (
    <ContentLayout data-panel='postingsHeader' isBrasil={isBrasil}>
      <h2 data-panel='postingHeader-title'>{t('panel.header.title.postings')}</h2>
      {showLockedSoftAlert && (
        <AlertContainer>
          <Alert onClose={closeLockedSoftAlert} isMobile dataLayerText='Saldo deudor'>
            <LockedSoftAlertMessage />
          </Alert>
        </AlertContainer>
      )}
      {showDuplicateAlert && (
        <AlertContainer>
          <Alert
            buttonText={t(`panel.avisos.alert.CTA.${duplicatedCount <= 1 ? 'single' : 'many'}`)}
            onClose={handleFilterDuplicates}
            isMobile
            optionalButtonText='Ignorar'
            onOptionalButtonPress={closeDuplicateAlert}
            dataLayerText='Avisos duplicados'
          >
            {t('panel.avisos.alert.single.duplicate1')} <strong>{duplicatedCount} </strong>
            {t(`panel.avisos.alert.${duplicatedCount === 1 ? 'single' : 'many'}.duplicate2`)}
          </Alert>
        </AlertContainer>
      )}
      {showLockedManualAlert && (
        <AlertContainer>
          <Alert dataLayerText='Cuenta bloqueada'>{t('panel.avisos.alert.lockedManual')}</Alert>
        </AlertContainer>
      )}
      {!!nearEndPostingCount && displayNearEndAlert && (
        <AlertContainer>
          <Alert
            buttonText={t(`panel.avisos.alert.postingsNearEnd.CTA.${nearEndPostingCount > 1 ? 'plural' : 'sing'}`)}
            onClose={handleNearEndCta}
            isMobile
            optionalButtonText='Ignorar'
            onOptionalButtonPress={() => setDisplayNearEndAlert(false)}
            dataLayerText='Avisos por vencer'
          >
            {t(`panel.avisos.alert.postingsNearEnd.${nearEndPostingCount > 1 ? 'plural' : 'sing'}`, {
              value: nearEndPostingCount
            })}
          </Alert>
        </AlertContainer>
      )}
      <ProductsButton
        handleClick={handleProducts}
        hasCredits={hasCredits}
        isTableOpen={drawerVisibility}
        isMobile
        isLoading={creditsState.loading}
        availableCount={getAvailableCreditsCount(branchCredits, globalCredits)}
        hasError={creditsState.fetchError}
      />

      {drawerVisibility && (
        <>
          {hasCredits ? (
            <Drawer isOpen={drawerVisibility} onClose={onClose}>
              <ProductsTable
                branchCredits={branchCredits}
                globalCredits={globalCredits && globalCredits.creditItems}
                hasCreditFromUnifiedPanel={hasCreditFromUnifiedPanel}
                isMobile
                hasError={creditsState.fetchError}
              />
            </Drawer>
          ) : (
            !creditsState.loading && <NoCredits>{t('panel.available.products.noCredits')}</NoCredits>
          )}
        </>
      )}
      {filterDrawer && (
        <Drawer isOpen={filterDrawer} coverAll>
          <Filtro onClose={() => setFilterDrawer(false)} />
        </Drawer>
      )}
      {(postingsCount || hasFilters) && (
        <>
          <ActionsRow>
            <FilterButton
              data-panel='postingsHeader_filtersBtn'
              onClick={() => {
                sendTrackingInfo('Panel', 'Mis avisos', 'Filtro-Desplegar filtros');
                // eslint-disable-next-line no-unused-expressions
                setFilterDrawer(true);
              }}
            >
              <img src={filterIcon} alt='Filtros' />
              <p>
                {t('panel.filters.title')}
                {appliedFiltersCount !== 0 && ` (${appliedFiltersCount})`}
              </p>
            </FilterButton>
            <Sort />
          </ActionsRow>
          <TotalCount data-panel='postingsHeader_totalCount'>
            {postingsCount === 1 ? t('panel.available.products.foundOne') : t('panel.available.products.found')}
            <strong>{`${formatAmount(postingsCount)} ${postingsCount === 1 ? t('posting') : t('postings')}`}</strong>
          </TotalCount>
          {showBannerUnifiedPanel && (
            <AlertContainer setMargin='16px 0 0 -16px' setWidth='100vw'>
              <Alert
                onClose={handleClickBannerVA}
                buttonText={t('panel.posting.banner.filters_UnifiedPanels.cta')}
                isWarning
                isMobile
                setPosition='absolute'
                setTop='40px'
                setLeft='210px'
              >
                {t('panel.posting.banner.filters_UnifiedPanels')}
              </Alert>
            </AlertContainer>
          )}
        </>
      )}
    </ContentLayout>
  );
}

export default PostingsHeader;
